// contexts/AppFunctionalityContext.tsx
import { createContext, ReactNode, useMemo, useState, useCallback, useRef } from "react";
import { mergeWith } from "lodash";
import { composeAppConfig, mergeLegacyConfigArrays } from "config/utils/composeAppConfig";
import { isAudienceLink, restrictAudienceConfig, assertAudienceMutationAllowed } from "utils/audienceSafety";
import defaultConfig from "config/appConfigFiles/default.json";
import {
  AppFunctionalityProps,
  AppConfig,
  AppConfigOverride,
  AppConfigSubNames,
  AppContext,
} from "types/appFunctionality";
import { getInternalAppType } from "utils/brandingUtils";
import { AI_APP_CONTEXT_SCHEMA_VERSION } from "config/config-general";

// ---- types for the context value (add your functions here) ----
type AppFunctionalityContextValue = AppFunctionalityProps & {
  isAudienceMode: boolean;
  setAnonymousSession: (anonymous: boolean) => void;
  isDomainReadOnly: () => boolean;
  assertDomainMutationAllowed: () => void;
  onChangeAppFunctionalityConfig: (cfg: AppConfigOverride, name?: AppConfigSubNames) => void;
  setSubscriptionConfig: (cfg: AppConfigOverride, name?: AppConfigSubNames) => void;
  setRoleConfig: (cfg: AppConfigOverride, name?: AppConfigSubNames) => void;
  resetAppFunctionalityConfig: () => void;
  mapPagePathToConfigName: (path: string) => keyof AppConfig["layout"] | "unknown";

  updateAppContext: (patch: Partial<AppContext>) => void;
  getAppContextSnapshot: () => AppContext;
};

// ---- initial value (non-null!) ----
const initialValue: AppFunctionalityContextValue = {
  isAudienceMode: false,
  setAnonymousSession: () => {},
  isDomainReadOnly: () => false,
  assertDomainMutationAllowed: () => {},
  config: { ...defaultConfig } as unknown as AppConfig,
  subConfigName: "unknown",
  roleConfig: undefined,
  appContext: { schemaVersion: AI_APP_CONTEXT_SCHEMA_VERSION } as AppContext,
  onChangeAppFunctionalityConfig: () => {},
  setSubscriptionConfig: () => {},
  setRoleConfig: () => {},
  resetAppFunctionalityConfig: () => {},
  mapPagePathToConfigName: () => "unknown",
  updateAppContext: () => {},
  getAppContextSnapshot: () => ({} as AppContext),
};

// ✅ EXPORT the context (non-nullable)
export const AppFunctionalityContext = createContext<AppFunctionalityContextValue>(initialValue);

type InternalState = {
  subConfigName: AppConfigSubNames | "unknown";
  subscriptionConfig?: AppConfigOverride;
  roleConfig?: AppConfigOverride;
};

type ProviderProps = { children: ReactNode };

export function AppFunctionalityProvider({ children }: ProviderProps) {
  const [state, setState] = useState<InternalState>({ subConfigName: "unknown" });
  // Link mode is independent of account/organisation resets and captured before URL consumption.
  const [audienceLink] = useState(() => isAudienceLink(window.location.search));
  const [anonymousSession, setAnonymousSessionState] = useState(false);
  const isAudienceMode = audienceLink || anonymousSession;
  const audienceModeRef = useRef(isAudienceMode);
  audienceModeRef.current = isAudienceMode;
  const setAnonymousSession = useCallback((anonymous: boolean) => {
    audienceModeRef.current = audienceLink || anonymous;
    setAnonymousSessionState(anonymous);
  }, [audienceLink]);
  const isDomainReadOnly = useCallback(() => audienceModeRef.current, []);
  const assertDomainMutationAllowed = useCallback(() => {
    assertAudienceMutationAllowed(audienceModeRef.current);
  }, []);
  const [appContext, setAppContext] = useState<AppContext>({ schemaVersion: AI_APP_CONTEXT_SCHEMA_VERSION } as AppContext);
  const appContextRef = useRef<AppContext>({ schemaVersion: AI_APP_CONTEXT_SCHEMA_VERSION } as AppContext);

  const internalAppType = getInternalAppType({ pathOrHost: window.location.hostname });

  const composedConfig = useMemo(
    () => {
      const config = composeAppConfig(internalAppType, state.subscriptionConfig, state.roleConfig);
      return isAudienceMode ? restrictAudienceConfig(config) : config;
    },
    [internalAppType, state.subscriptionConfig, state.roleConfig, isAudienceMode],
  );

  const onChangeAppFunctionalityConfig = useCallback(
    (newConfig: AppConfigOverride, subConfigName?: AppConfigSubNames) => {
      const isRoleConfig = !!newConfig?.roleType;
      setState((prev) => ({
        subConfigName: isRoleConfig ? prev.subConfigName : subConfigName ?? newConfig.name ?? "unknown",
        // Replace the previous subscription; otherwise profile-only options survive a switch.
        subscriptionConfig: isRoleConfig ? prev.subscriptionConfig : newConfig,
        roleConfig: isRoleConfig
          ? mergeWith({}, prev.roleConfig ?? {}, newConfig, mergeLegacyConfigArrays)
          : prev.roleConfig,
      }));
    },
    [],
  );

  const setSubscriptionConfig = useCallback((cfg: AppConfigOverride, subConfigName?: AppConfigSubNames) => {
    setState((prev) => ({ ...prev, subConfigName: subConfigName ?? cfg.name ?? "unknown", subscriptionConfig: cfg }));
  }, []);

  const setRoleConfig = useCallback((cfg: AppConfigOverride) => {
    setState((prev) => ({
      ...prev,
      roleConfig: mergeWith({}, prev.roleConfig ?? {}, cfg, mergeLegacyConfigArrays),
    }));
  }, []);

  const resetAppFunctionalityConfig = useCallback(() => {
    setState({ subConfigName: "unknown" });
  }, []);

  const mapPagePathToConfigName = (path: string): keyof AppConfig["layout"] | "unknown" => {
    switch (path) {
      case "/":
      case "/home-page":
        return "homePage";
      case "/map-page":
        return "mapPage";
      case "/lists-page":
        return "listsPage";
      case "/projects-page":
        return "projectsPage";
      case "/files-page":
        return "filesPage";
      case "/estates-page":
        return "estatesPage";
      case "/team-and-contacts-page":
        return "teamAndContactsPage";
      case "/account-page":
        return "accountPage";
      case "/profile-page":
        return "profilePage";
      default:
        return "unknown";
    }
  };

  const updateAppContext = useCallback((patch: Partial<AppContext>) => {
    setAppContext((prev) => {
      const next: AppContext = {
        ...prev,
        ...patch,
        route: patch.route ?? prev.route,
        ui: {
          ...prev.ui,
          ...patch.ui,
        },
        domain: {
          ...prev.domain,
          ...patch.domain,
        },
      };

      appContextRef.current = next;
      return next;
    });
  }, []);

  const getAppContextSnapshot = useCallback(() => {
    return sanitizeAppContext({
      ...appContextRef.current,
      app: {
        ...appContextRef.current.app,
        internalAppType: getInternalAppType({ pathOrHost: window.location.hostname }),
      },
    });
  }, []);

  return (
    <AppFunctionalityContext.Provider
      value={{
        isAudienceMode,
        setAnonymousSession,
        isDomainReadOnly,
        assertDomainMutationAllowed,
        config: composedConfig,
        subConfigName: composedConfig.baseConfig === "george" ? composedConfig.name : state.subConfigName,
        appContext,
        onChangeAppFunctionalityConfig,
        setSubscriptionConfig,
        setRoleConfig,
        resetAppFunctionalityConfig,
        mapPagePathToConfigName,
        updateAppContext,
        getAppContextSnapshot,
      }}
    >
      {children}
    </AppFunctionalityContext.Provider>
  );
}

function sanitizeAppContext<T>(value: T): T {
  return JSON.parse(
    JSON.stringify(value, (_key, currentValue) => {
      if (currentValue === undefined) return undefined;
      if (typeof currentValue === "function") return undefined;
      if (typeof currentValue === "symbol") return undefined;
      return currentValue;
    }),
  );
}
